import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { SecretCard } from './SecretCard';
import type { Secret } from '@/hooks/useSecrets';
import { Terminal, Keyboard } from 'lucide-react';
import { Command } from '@/components/ui/code-block';

interface SecretsListProps {
  secrets: Secret[];
  selectedKeys: Set<string>;
  revealedSecrets: Map<string, string>;
  loadingReveal: Set<string>;
  editingKey: string | null;
  isSaving: boolean;
  environmentSlug: string;
  onSelect: (key: string, selected: boolean) => void;
  onReveal: (key: string) => void;
  onHide: (key: string) => void;
  onCopy: (key: string) => void;
  onEdit: (key: string) => void;
  onSave: (key: string, value: string) => void;
  onCancelEdit: () => void;
  onOpenDrawer: (key: string) => void;
  onDelete: (key: string) => void;
  searchQuery: string;
}

export function SecretsList({
  secrets,
  revealedSecrets,
  loadingReveal,
  editingKey,
  isSaving,
  environmentSlug,
  onReveal,
  onHide,
  onCopy,
  onEdit,
  onSave,
  onCancelEdit,
  onOpenDrawer,
  onDelete,
  searchQuery,
}: SecretsListProps) {
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredSecrets = searchQuery
    ? secrets.filter((s) => s.key.toLowerCase().includes(searchQuery.toLowerCase()))
    : secrets;

  // Reset focus when search or list changes - intentional synchronization
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFocusedIndex(-1);
  }, [searchQuery, secrets.length]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (editingKey || e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      const maxIndex = filteredSecrets.length - 1;

      switch (e.key) {
        case 'ArrowDown':
        case 'j':
          e.preventDefault();
          setFocusedIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
          break;
        case 'ArrowUp':
        case 'k':
          e.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex <= maxIndex) {
            onOpenDrawer(filteredSecrets[focusedIndex].key);
          }
          break;
        case 'e':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex <= maxIndex) {
            onEdit(filteredSecrets[focusedIndex].key);
          }
          break;
        case 'c':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex <= maxIndex) {
            onCopy(filteredSecrets[focusedIndex].key);
          }
          break;
        case 'r':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex <= maxIndex) {
            const key = filteredSecrets[focusedIndex].key;
            if (revealedSecrets.has(key)) {
              onHide(key);
            } else {
              onReveal(key);
            }
          }
          break;
        case 'Escape':
          e.preventDefault();
          setFocusedIndex(-1);
          containerRef.current?.blur();
          break;
        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setFocusedIndex(maxIndex);
          break;
      }
    },
    [editingKey, filteredSecrets, focusedIndex, onCopy, onEdit, onHide, onOpenDrawer, onReveal, revealedSecrets]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (focusedIndex >= 0) {
      const focusedElement = containerRef.current?.querySelector(`[data-index="${focusedIndex}"]`);
      focusedElement?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [focusedIndex]);

  if (secrets.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="rounded-xl border border-border/50 bg-card/30 backdrop-blur-sm p-16 text-center"
      >
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 mb-6"
        >
          <Terminal className="h-8 w-8 text-primary" />
        </motion.div>
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-base font-medium mb-2"
        >
          No secrets yet
        </motion.p>
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-sm text-muted-foreground mb-8 max-w-sm mx-auto"
        >
          Push secrets from your local .env file or add them manually using the button above
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Command>{`keyenv push --env ${environmentSlug}`}</Command>
        </motion.div>
      </motion.div>
    );
  }

  if (filteredSecrets.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="rounded-xl border border-border/50 bg-card/30 backdrop-blur-sm p-12 text-center"
      >
        <p className="text-sm text-muted-foreground">
          No secrets matching "<span className="text-foreground font-medium">{searchQuery}</span>"
        </p>
      </motion.div>
    );
  }

  return (
    <div className="space-y-3">
      <motion.div
        ref={containerRef}
        tabIndex={0}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="rounded-xl border border-border/50 bg-card/30 backdrop-blur-sm overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2 focus:ring-offset-background transition-shadow"
        onFocus={() => focusedIndex === -1 && filteredSecrets.length > 0 && setFocusedIndex(0)}
      >
        <AnimatePresence mode="popLayout">
          {filteredSecrets.map((secret, index) => (
            <motion.div
              key={secret.key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ 
                duration: 0.2,
                delay: Math.min(index * 0.03, 0.3),
              }}
            >
              <SecretCard
                secret={secret}
                isRevealed={revealedSecrets.has(secret.key)}
                revealedValue={revealedSecrets.get(secret.key)}
                isEditing={editingKey === secret.key}
                isLoadingReveal={loadingReveal.has(secret.key)}
                isSaving={isSaving}
                isFocused={focusedIndex === index}
                dataIndex={index}
                onReveal={onReveal}
                onHide={onHide}
                onCopy={onCopy}
                onEdit={onEdit}
                onSave={onSave}
                onCancelEdit={onCancelEdit}
                onOpenDrawer={onOpenDrawer}
                onDelete={onDelete}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="flex items-center justify-center gap-4 text-[11px] text-muted-foreground/60 py-2"
      >
        <span className="flex items-center gap-1.5">
          <Keyboard className="h-3 w-3" />
          Navigate
          <kbd className="kbd">↑</kbd>
          <kbd className="kbd">↓</kbd>
        </span>
        <span className="flex items-center gap-1.5">
          Open
          <kbd className="kbd">↵</kbd>
        </span>
        <span className="flex items-center gap-1.5">
          Copy
          <kbd className="kbd">c</kbd>
        </span>
        <span className="flex items-center gap-1.5">
          Reveal
          <kbd className="kbd">r</kbd>
        </span>
      </motion.div>
    </div>
  );
}
